import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../../api/client";
import {
  fetchGuestbook,
  postGuestbookEntry,
  MAX_GUESTBOOK_MESSAGE_LENGTH,
  MAX_GUESTBOOK_NAME_LENGTH,
  type GuestbookEntry,
} from "../../api/guestbook";
import { useAppConfig } from "../../hooks/useAppConfig";
import { Button } from "../../design-system/Button";
import styles from "./GuestbookPage.module.css";

// Same key as the upload form, so a guest who already typed their name there
// does not have to type it again here (and vice versa).
const NAME_STORAGE_KEY = "mariage_uploader_name";
const DRAFT_STORAGE_KEY = "mariage_guestbook_draft";
const POLL_INTERVAL_MS = 30_000;

function readStorage(key: string): string {
  try {
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

function writeStorage(key: string, value: string) {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    /* storage can be unavailable (private mode) - the form still works */
  }
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

export function GuestbookPage() {
  const config = useAppConfig();
  const [entries, setEntries] = useState<GuestbookEntry[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [name, setName] = useState(() => readStorage(NAME_STORAGE_KEY));
  // The unsent message is kept across reloads so a refresh never loses what a guest was writing.
  const [message, setMessage] = useState(() => readStorage(DRAFT_STORAGE_KEY));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justSent, setJustSent] = useState(false);

  const load = useCallback(async () => {
    try {
      setEntries(await fetchGuestbook());
      setLoaded(true);
    } catch {
      /* transient network error: the next poll retries */
    }
  }, []);

  useEffect(() => {
    void load();
    const interval = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  function changeName(value: string) {
    setName(value);
    writeStorage(NAME_STORAGE_KEY, value);
  }

  function changeMessage(value: string) {
    setMessage(value);
    writeStorage(DRAFT_STORAGE_KEY, value);
    setJustSent(false);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    setError(null);
    try {
      await postGuestbookEntry(name.trim(), message.trim());
      setMessage("");
      writeStorage(DRAFT_STORAGE_KEY, "");
      setJustSent(true);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Impossible d'envoyer ton message, réessaie.");
    } finally {
      setSending(false);
    }
  }

  const canSend = name.trim().length > 0 && message.trim().length > 0 && !sending;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Livre d'or</h1>
        <p className={styles.couple}>{config.coupleNames}</p>
        <Link to="/" className={styles.backLink}>
          <span className={styles.ctaTitle}>📷 Retour aux photos</span>
          <span className={styles.ctaText}>Partage tes plus beaux moments de la journée</span>
        </Link>
      </header>

      <form className={styles.form} onSubmit={(e) => void handleSubmit(e)}>
        <input
          className={styles.input}
          type="text"
          placeholder="Ton prénom"
          value={name}
          onChange={(e) => changeName(e.target.value)}
          maxLength={MAX_GUESTBOOK_NAME_LENGTH}
          required
        />
        <textarea
          className={styles.textarea}
          placeholder="Écris-nous un petit mot…"
          value={message}
          onChange={(e) => changeMessage(e.target.value)}
          maxLength={MAX_GUESTBOOK_MESSAGE_LENGTH}
          rows={5}
          required
        />
        <div className={styles.formFooter}>
          <span className={styles.counter}>
            {message.length}/{MAX_GUESTBOOK_MESSAGE_LENGTH}
          </span>
          <Button type="submit" disabled={!canSend}>
            {sending ? "Envoi…" : "✍️ Signer le livre d'or"}
          </Button>
        </div>
        {error && <p className={styles.error}>{error}</p>}
        {justSent && <p className={styles.thanks}>Merci pour ton message 💛</p>}
      </form>

      <section>
        <h2 className={styles.listTitle}>Les mots de nos invités ({entries.length})</h2>
        {loaded && entries.length === 0 && <p className={styles.empty}>Sois le premier à laisser un mot ✨</p>}
        <ul className={styles.list}>
          {entries.map((entry) => (
            <li key={entry.id} className={styles.entry}>
              <p className={styles.message}>{entry.message}</p>
              <p className={styles.signature}>
                — {entry.name}, <time dateTime={new Date(entry.createdAt).toISOString()}>{formatDate(entry.createdAt)}</time>
              </p>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
